import React, { useState, useEffect } from 'react';
import { browserHistory } from 'react-router';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router';
import Tooltip from '@mui/material/Tooltip';
import { getEnvironmentSettings } from 'services/environment';

import { includes } from 'lodash';

import { AUDIENCE_CATEGORY_HIERARCHY } from 'states/resources/audiences/business-logic';

import OverallTableDashboard from '../../table-dashboard';
import { useOverallTableDashboardData } from './hook';

// Tabs under Audiences page on left menu
export function getAvailableCategories() {
    const excludedAudiences = getEnvironmentSettings().excludedAudiences();

    const categoriesToUse = AUDIENCE_CATEGORY_HIERARCHY.map(category => {
        // Remove custom_audiences section from Pelmorex Data
        if (category.value === 'pelmorex_data' && category.sections) {
            return {
                ...category,
                sections: category.sections.filter(section => {
                    return section.value !== 'custom_audiences';
                }),
            };
        }
        return category;
    });

    return categoriesToUse.map(availableCategory => {
        const excludedAudiencesList = excludedAudiences[availableCategory.value];
        if (undefined === excludedAudiencesList) {
            return availableCategory;
        }
        availableCategory.categories = _.filter(availableCategory.categories, category => {
            if (_.includes(excludedAudiencesList, category.source)) {
                return false;
            }
            return true;
        });
        return availableCategory;
    });
}

const tabsRestructured = [
    {
        label: 'All Audiences',
        value: 'all',
    },
    {
        label: 'Pelmorex Data',
        value: 'pelmorex_data',
    },
    {
        label: 'Your Custom Audiences',
        value: 'custom_audiences',
    },
    {
        label: 'Partnerships',
        value: 'partnerships',
    },
    {
        label: 'Advertiser Data',
        value: 'advertiser_data',
    },
];

const useStyles = makeStyles({
    sectionTab: {
        fontSize: '14px',
        textTransform: 'uppercase',
    },
    audienceCardItem: {
        flexShrink: 0,
    },
    image: {
        width: '70%',
    },
    overlay: {
        visibility: 'hidden',
        position: 'absolute',
        height: '100%',
        width: '100%',
        opacity: '0.95',
        backgroundColor: '#000',
        color: '#fff',
        padding: '20px 15px 0 15px',
    },
    root: {
        '&:hover > div': {
            visibility: 'visible',
        },
        position: 'relative',
    },
    icon: {
        width: '3.5em',
        height: '3.5em',
    },
    iconColor: {
        color: '#098679',
    },
    categoryCard: {
        height: '280px',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'transform 0.2s ease-in-out',
        backgroundColor: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        '&:hover': {
            transform: 'scale(1.05)',
        },
    },
    mapSection: {
        height: '60%',
        backgroundColor: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        width: '100%',
    },
    iconContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& > *': {
            width: '100% !important',
            height: 'auto !important',
            maxHeight: '100%',
            // objectFit: 'contain',
        },
        '& svg': {
            width: '100%',
            height: '100%',
        },
        '& img': {
            width: '100%',
            height: 'auto',
            maxHeight: '100%',
            padding: '16px',
            // objectFit: 'contain',
        },
    },
    iconPlaceholder: {
        width: '60px',
        height: '60px',
        backgroundColor: '#ddd',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        color: '#666',
    },
    infoSection: {
        height: '40%',
        backgroundColor: 'white',
        color: '#333',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    categoryTitle: {
        fontWeight: 'bold',
        fontSize: '1.1rem',
        marginBottom: '8px',
        lineHeight: 1.2,
    },
    categoryDescription: {
        fontSize: '0.85rem',
        lineHeight: 1.3,
        opacity: 0.9,
    },
});

const CardGrid = ({ categories }) => {
    const classes = useStyles();

    return (
        <Grid container spacing={2}>
            {_.map(categories, category => (
                <Grid item xs={3} key={category.label}>
                    <RouterLink
                        color="inherit"
                        component={Link}
                        to={category.link}
                        underline="none"
                    >
                        <Card className={classes.categoryCard}>
                            <Box className={classes.mapSection}>
                                <Box className={classes.iconContainer}>{category.icon}</Box>
                            </Box>
                            <Box className={classes.infoSection}>
                                <Typography className={classes.categoryTitle}>
                                    {category.label}
                                </Typography>
                                <Typography className={classes.categoryDescription}>
                                    {category.tooltip}
                                </Typography>
                            </Box>
                        </Card>
                    </RouterLink>
                </Grid>
            ))}
        </Grid>
    );
};

const OverallDashboardV2 = () => {
    const [currentTab, setCurrentTab] = useState('all');
    const { restrictedSegments, orgId } = useOverallTableDashboardData();

    const tabsToUse = tabsRestructured;

    const classes = useStyles();

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const audienceTab = urlParams.get('audienceTab');
        if (audienceTab && audienceTab !== currentTab) {
            setCurrentTab(audienceTab);
            browserHistory.push('/audiences/all');
        }
    }, []);

    const onChangeTab = (event, value) => {
        setCurrentTab(value);
    };

    const audienceCategories = getAvailableCategories(classes);

    const selectedType =
        currentTab !== 'all'
            ? audienceCategories.find(category => category.value === currentTab)
            : null;

    const getTabTooltip = tabValue => {
        const tab = audienceCategories.find(category => category.value === tabValue);
        return tab
            ? tab.description
            : 'Gain insight on your target audience by browsing through our directory of audiences.';
    };

    return (
        <Container maxWidth="xl">
            <Box mt={2}>
                <Typography variant="h3" gutterBottom>
                    Audiences
                </Typography>
            </Box>
            <Tabs
                value={currentTab}
                onChange={onChangeTab}
                indicatorColor="primary"
                textColor="primary"
            >
                {_.map(tabsToUse, tab => (
                    <Tab
                        className={classes.sectionTab}
                        key={tab.value}
                        value={tab.value}
                        label={
                            <Tooltip
                                title={
                                    <Typography variant="body2">
                                        {getTabTooltip(tab.value)}
                                    </Typography>
                                }
                            >
                                <span>{tab.label}</span>
                            </Tooltip>
                        }
                    />
                ))}
            </Tabs>
            <div>
                {currentTab === 'all' ? (
                    <Box mb={3}>
                        <OverallTableDashboard />
                    </Box>
                ) : null}
                {selectedType ? (
                    <Box mb={3} key={selectedType.type}>
                        <Box display="flex" flexDirection="column" mb={4} mt={4}>
                            <Typography color="textPrimary" variant="h4" gutterBottom>
                                {selectedType.type}
                            </Typography>
                            <Typography color="textSecondary" variant="h6" gutterBottom>
                                {selectedType.description}
                            </Typography>
                        </Box>
                        {selectedType.categories && (
                            <CardGrid
                                categories={selectedType.categories.filter(
                                    category =>
                                        !includes(restrictedSegments, category.source) &&
                                        (!category.showOn || category.showOn(orgId))
                                )}
                            />
                        )}
                        {selectedType.sections &&
                            selectedType.sections.map(section => {
                                return (
                                    <React.Fragment key={section.label}>
                                        <Box display="flex" flexDirection="column" mb={2} mt={4}>
                                            <Typography
                                                color="textPrimary"
                                                variant="h6"
                                                gutterBottom
                                            >
                                                {section.label}
                                            </Typography>
                                        </Box>
                                        {section.categories && (
                                            <CardGrid
                                                categories={section.categories.filter(
                                                    categoty =>
                                                        !includes(
                                                            restrictedSegments,
                                                            categoty.source
                                                        )
                                                )}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                    </Box>
                ) : null}
            </div>
        </Container>
    );
};

export default OverallDashboardV2;
